import React, { useState, useRef } from "react";
import { supabase } from "../lib/supabaseClient.ts";
import { predictPlantDisease } from "../utils/tfPredict.ts"; 

interface AddCropHealthReportModalProps {
  open: boolean;
  onClose: () => void;
  onReportAdded: () => void;
  userId: string
  farms: { id: string; name: string }[];
}

const statusOptions = [
  "Healthy",
  "Needs Attention",
  "Diseased",
  "Pest Detected",
  "Unknown"
];

// Helper to prettify class names for UI
function prettyClassName(str: string) {
  return str.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// Improved mapping for production
function mapPredictionToStatus(className: string, probability: number): string {
  const lower = className.toLowerCase();
  // Strong match for disease or pest at high confidence
  if ((lower.includes("disease") || lower.includes("blight") || lower.includes("mildew")) && probability > 0.5)
    return "Diseased";
  if ((lower.includes("pest") || lower.includes("insect") || lower.includes("butterfly") || lower.includes("fly")) && probability > 0.5)
    return "Pest Detected";
  if (lower.includes("healthy") && probability > 0.5)
    return "Healthy";
  if (probability < 0.5)
    return "Unknown";
  // If confidence is low or label is generic, require user attention
  return "Needs Attention";
}

function aiNoteText(className: string, probability: number): string {
  if (probability < 0.5) {
    return `AI could not confidently classify this image. Please review. (Top suggestion: "${prettyClassName(className)}" at ${(probability * 100).toFixed(1)}%)`;
  }
  return `AI suggests: "${prettyClassName(className)}" with ${(probability * 100).toFixed(1)}% confidence.`;
}

export default function AddCropHealthReportModal({
  open,
  onClose,
  onReportAdded,
  userId,
  farms
}: AddCropHealthReportModalProps) {
  const [farmId, setFarmId] = useState(farms.length === 1 ? farms[0].id : "");
  const [status, setStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiPredictions, setAiPredictions] = useState<{className: string; probability: number}[]>([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [aiFilled, setAiFilled] = useState(false);
  const [aiConfidence, setAiConfidence] = useState<number | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  // Handle image selection and preview + AI analysis
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setImageFile(file || null);
    setAiPredictions([]);
    setAiFilled(false);
    setAiConfidence(null);
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result as string);
        setTimeout(() => {
          if (imgRef.current) runAiPrediction(imgRef.current);
        }, 100);
      };
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };

  // AI prediction and auto-fill logic
  const runAiPrediction = async (imgEl: HTMLImageElement) => {
    setAnalyzing(true);
    setError(null);
    try {
      const preds = await predictPlantDisease(imgEl);
      setAiPredictions(preds);

      if (preds.length > 0) {
        const top = preds[0];
        // Only auto-fill status/notes if confidence is reasonable
        setStatus(mapPredictionToStatus(top.className, top.probability));
        setNotes(aiNoteText(top.className, top.probability));
        setAiConfidence(top.probability);
        setAiFilled(true);
      }
    } catch {
      setError("AI analysis failed. Please try again.");
    }
    setAnalyzing(false);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setAiPredictions([]);
    setAiFilled(false);
    setAiConfidence(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!farmId || !status) {
      setError("Farm and status are required.");
      return;
    }
    setLoading(true);
    setError(null);

    let image_url: string | null = null;

    // 1. Upload image if present
    if (imageFile) {
      const fileExt = imageFile.name.split('.').pop();
      const filePath = `user-${userId}/${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage
        .from("crop-health-images")
        .upload(filePath, imageFile, { upsert: true });
      if (uploadError) {
        setLoading(false);
        setError("Image upload failed: " + uploadError.message);
        return;
      }
      // Get public URL
      const { data } = supabase.storage.from("crop-health-images").getPublicUrl(filePath);
      image_url = data.publicUrl;
    }

    // 2. Insert report in DB
    const { error: dbError } = await supabase.from("crop_health_reports").insert([
      {
        farm_id: farmId,
        user_id: userId,
        status,
        notes,
        image_url,
        ai_prediction: aiPredictions.length ? JSON.stringify(aiPredictions) : null,
      }
    ]);
    setLoading(false);
    if (dbError) {
      setError(dbError.message);
    } else {
      setFarmId(farms.length === 1 ? farms[0].id : "");
      setStatus("");
      setNotes("");
      handleRemoveImage();
      onReportAdded();
      onClose();
    }
  };

  // "Quick Report" — submit as soon as AI finished and fields are filled
  const handleQuickReport = async () => {
    if (!farmId || !status) {
      setError("Farm and status are required.");
      return;
    }
    setLoading(true);
    setError(null);

    let image_url: string | null = null;

    if (imageFile) {
      const fileExt = imageFile.name.split('.').pop();
      const filePath = `user-${userId}/${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage
        .from("crop-health-images")
        .upload(filePath, imageFile, { upsert: true });
      if (uploadError) {
        setLoading(false);
        setError("Image upload failed: " + uploadError.message);
        return;
      }
      const { data } = supabase.storage.from("crop-health-images").getPublicUrl(filePath);
      image_url = data.publicUrl;
    }

    const { error: dbError } = await supabase.from("crop_health_reports").insert([
      {
        farm_id: farmId,
        user_id: userId,
        status,
        notes,
        image_url,
        ai_prediction: aiPredictions.length ? JSON.stringify(aiPredictions) : null,
      }
    ]);
    setLoading(false);
    if (dbError) {
      setError(dbError.message);
    } else {
      setFarmId(farms.length === 1 ? farms[0].id : "");
      setStatus("");
      setNotes("");
      handleRemoveImage();
      onReportAdded();
      onClose();
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <form
        className="w-full max-w-md p-8 bg-white shadow-lg rounded-xl"
        onSubmit={handleSubmit}
      >
        <h2 className="mb-4 text-xl font-bold">Add Crop Health Report</h2>
        <div className="mb-3">
          <label className="block mb-1 font-medium">Farm</label>
          <select
            className="w-full p-2 border rounded"
            value={farmId}
            onChange={e => setFarmId(e.target.value)}
            required
            disabled={farms.length === 1}
          >
            <option value="">Select Farm</option>
            {farms.map(farm => (
              <option value={farm.id} key={farm.id}>{farm.name}</option>
            ))}
          </select>
        </div>
        <div className="mb-3">
          <label className="block mb-1 font-medium">Status</label>
          <select
            className="w-full p-2 border rounded"
            value={status}
            onChange={e => setStatus(e.target.value)}
            required
            style={{
              backgroundColor: status === "Diseased" ? "#ffe5e5" :
                status === "Healthy" ? "#e5ffe5" :
                status === "Needs Attention" ? "#fffbe5" :
                status === "Pest Detected" ? "#f9e5ff" : "white"
            }}
          >
            <option value="">Select Status</option>
            {statusOptions.map(opt => (
              <option value={opt} key={opt}>{opt}</option>
            ))}
            {/* AI-driven: also allow user to pick from AI classes */}
            {aiPredictions
              .filter(
                (ap) => !statusOptions.includes(mapPredictionToStatus(ap.className, ap.probability))
              )
              .map((ap) => (
                <option value={ap.className} key={ap.className}>
                  {prettyClassName(ap.className)}
                </option>
              ))}
          </select>
          {aiConfidence !== null && (
            <div className="mt-1 text-xs">
              AI Confidence: <span style={{
                color: aiConfidence >= 0.5 ? "#22bb33" : "#e67e22"
              }}>{(aiConfidence * 100).toFixed(1)}%</span>
              {aiConfidence < 0.5 && (
                <span className="ml-1 text-red-600">(Low confidence, please review status)</span>
              )}
            </div>
          )}
        </div>
        <div className="mb-3">
          <label className="block mb-1 font-medium">Notes (editable)</label>
          <textarea
            className="w-full p-2 border rounded"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={3}
          />
        </div>
        <div className="mb-3">
          <label className="block mb-1 font-medium">Image (optional)</label>
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handleImageChange}
            className="block w-full text-sm text-gray-900 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
          />
          {imagePreview && (
            <div className="relative mt-2">
              <img
                src={imagePreview}
                alt="Preview"
                className="border rounded max-h-40"
                ref={imgRef}
                style={{ display: "block", maxWidth: "100%" }}
                onLoad={() => {
                  if (imgRef.current) runAiPrediction(imgRef.current);
                }}
                crossOrigin="anonymous"
              />
              <button
                type="button"
                className="absolute p-1 text-red-600 bg-white rounded-full top-2 right-2 bg-opacity-80 hover:bg-red-100"
                onClick={handleRemoveImage}
                title="Remove image"
              >✕</button>
              {analyzing && (
                <div className="absolute px-2 py-1 text-xs text-green-700 rounded left-2 top-2 bg-white/80">
                  Analyzing crop…
                </div>
              )}
              {aiPredictions.length > 0 && !analyzing && (
                <ul className="absolute px-2 py-1 text-xs rounded shadow left-2 bottom-2 bg-white/90">
                  {aiPredictions.slice(0, 3).map((p, i) => (
                    <li key={i}>
                      {prettyClassName(p.className)}: {(p.probability * 100).toFixed(1)}%
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        {error && <div className="mb-2 text-red-500">{error}</div>}
        <div className="flex justify-between gap-2 mt-4">
          <button type="button" className="px-4 py-2 bg-gray-200 rounded" onClick={onClose} disabled={loading}>Cancel</button>
          <div className="flex gap-2">
            {/* Quick Report button appears after AI fills fields */}
            {aiFilled && (
              <button
                type="button"
                className="px-4 py-2 text-white bg-blue-600 rounded"
                onClick={handleQuickReport}
                disabled={loading}
                title="Submit immediately with AI-suggested info"
              >
                Quick Report
              </button>
            )}
            <button type="submit" className="px-4 py-2 text-white bg-green-600 rounded" disabled={loading}>
              {loading ? "Saving..." : "Add Report"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}